'use client'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'

export const useUnsavedChangesGuard = (isDirty: boolean) => {
  const router = useRouter()
  const [pendingUrl, setPendingUrl] = useState<string | null>(null)
  const isDirtyRef = useRef(isDirty)
  const bypassRef = useRef(false)

  isDirtyRef.current = isDirty

  // Block browser refresh / tab close
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirtyRef.current) {
        e.preventDefault()
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])

  // Block <Link> clicks — capture phase fires before Next.js Link's onClick.
  // Next.js Link checks event.defaultPrevented before calling router.push.
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!isDirtyRef.current || bypassRef.current) {
        return
      }
      const anchor = (e.target as Element).closest('a[href]')
      if (!anchor) {
        return
      }
      const href = anchor.getAttribute('href')
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return
      }
      if (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('//')) {
        return
      }
      const currentPath = window.location.pathname + window.location.search
      if (href === currentPath || href === window.location.pathname) {
        return
      }
      e.preventDefault()
      setPendingUrl(href)
    }
    document.addEventListener('click', handleClick, true)
    return () => document.removeEventListener('click', handleClick, true)
  }, [])

  // Block programmatic navigation (router.push / window.history.pushState)
  useEffect(() => {
    const originalPushState = window.history.pushState.bind(window.history)

    window.history.pushState = (state: unknown, title: string, url?: string | URL | null) => {
      if (isDirtyRef.current && !bypassRef.current && url !== null && url !== undefined) {
        const urlString = url.toString()
        const currentPath = window.location.pathname + window.location.search
        if (urlString !== window.location.href && urlString !== currentPath) {
          setTimeout(() => setPendingUrl(urlString), 0)
          return
        }
      }
      originalPushState(state, title, url)
    }

    const handlePopState = () => {
      if (isDirtyRef.current && !bypassRef.current) {
        window.history.pushState(null, '', window.location.href)
        setTimeout(() => setPendingUrl('__back__'), 0)
      }
    }
    window.addEventListener('popstate', handlePopState)

    return () => {
      window.history.pushState = originalPushState
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  const confirmNavigation = () => {
    if (!pendingUrl) {
      return
    }
    bypassRef.current = true
    if (pendingUrl === '__back__') {
      window.history.go(-1)
    } else {
      router.push(pendingUrl)
    }
    setPendingUrl(null)
    setTimeout(() => {
      bypassRef.current = false
    }, 500)
  }

  const cancelNavigation = () => setPendingUrl(null)

  return {
    isNavigationBlocked: !!pendingUrl,
    confirmNavigation,
    cancelNavigation
  }
}
